/**
 * Footer.jsx - Site Footer
 * 100% Blueprint CMS driven - NO hardcoded text
 * Dark premium theme
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import supabase from '../lib/supabase';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

const COUNTRIES = [
  { code: 'IT', name: 'Italia' },
  { code: 'US', name: 'United States' },
  { code: 'ES', name: 'España' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Deutschland' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CH', name: 'Svizzera' },
  { code: 'AT', name: 'Österreich' },
  { code: 'AE', name: 'UAE' },
  { code: 'OTHER', name: 'Other' },
];

const Footer = () => {
  const { i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [country, setCountry] = useState(() => localStorage.getItem('userCountry') || '');
  
  // Blueprint CMS State
  const [branding, setBranding] = useState({
    site_name: 'SlabReality',
    logo_url: '',
    footer_description: 'A professional environment for architects and interior designers.',
    footer_description_it: 'Un ambiente professionale per architetti e interior designer.',
    copyright_text: '',
    social_links: {}
  });
  
  const [footerPages, setFooterPages] = useState({
    explore: [],
    resources: [],
    company: [],
    legal: []
  });

  // Fetch data from Blueprint CMS
  useEffect(() => {
    const fetchBlueprintData = async () => {
      try {
        // Try RPC first
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_blueprint_config', { p_lang: 'it' });
        
        if (!rpcError && rpcData) {
          console.log('Footer: Using Blueprint RPC config');
          
          if (rpcData.branding) {
            setBranding({
              site_name: rpcData.branding.site_name || 'SlabReality',
              logo_url: rpcData.branding.logo_url || '',
              primary_color: rpcData.branding.primary_color || '#b08b5c'
            });
          }
          
          if (rpcData.footer?.pages) {
            setFooterPages({
              explore: rpcData.footer.pages.explore || [],
              resources: rpcData.footer.pages.resources || [],
              company: rpcData.footer.pages.company || [],
              legal: rpcData.footer.pages.legal || []
            });
            return;
          }
        }
        
        // Fallback: Direct table queries
        console.log('Footer: RPC failed, trying direct query');
        
        // Fetch branding
        const { data: brandingData } = await supabase
          .from('blueprint_branding')
          .select('*')
          .limit(1)
          .single();
        
        if (brandingData) {
          setBranding(brandingData);
        }

        // Fetch footer menu pages
        const { data: pagesData } = await supabase
          .from('blueprint_pages')
          .select('slug, title, title_it, footer_menu_column')
          .eq('show_in_footer_menu', true)
          .eq('is_published', true)
          .order('footer_menu_order', { ascending: true });
        
        if (pagesData && pagesData.length > 0) {
          const grouped = {
            explore: pagesData.filter(p => p.footer_menu_column === 'explore'),
            resources: pagesData.filter(p => p.footer_menu_column === 'resources'),
            company: pagesData.filter(p => p.footer_menu_column === 'company'),
            legal: pagesData.filter(p => p.footer_menu_column === 'legal')
          };
          setFooterPages(grouped);
        } else {
          // Final fallback: Default navigation
          console.log('Footer: No CMS pages found, using default navigation');
          setFooterPages({
            explore: [
              { slug: 'inspiration', title: 'Inspiration', title_it: 'Ispirazione' },
              { slug: 'pricing', title: 'Pricing', title_it: 'Prezzi' },
              { slug: 'materials', title: 'Materials', title_it: 'Materiali' },
              { slug: 'visual-search', title: 'Visual Search', title_it: 'Ricerca Visiva' }
            ],
            resources: [
              { slug: 'support', title: 'Support', title_it: 'Supporto' },
              { slug: 'projects', title: 'Projects', title_it: 'Progetti' },
              { slug: 'dna-compare', title: 'DNA Compare', title_it: 'Confronta DNA' }
            ],
            company: [
              { slug: 'about', title: 'About', title_it: 'Chi Siamo' },
              { slug: 'contact', title: 'Contact', title_it: 'Contatti' }
            ],
            legal: [
              { slug: 'privacy', title: 'Privacy', title_it: 'Privacy' },
              { slug: 'terms', title: 'Terms', title_it: 'Termini di Servizio' }
            ]
          });
        }
      } catch (err) {
        console.error('Footer: Error fetching Blueprint data:', err);
        // Use defaults on error
        setFooterPages({
          explore: [
            { slug: 'inspiration', title: 'Inspiration', title_it: 'Ispirazione' },
            { slug: 'pricing', title: 'Pricing', title_it: 'Prezzi' },
            { slug: 'materials', title: 'Materials', title_it: 'Materiali' },
            { slug: 'visual-search', title: 'Visual Search', title_it: 'Ricerca Visiva' }
          ],
          resources: [
            { slug: 'support', title: 'Support', title_it: 'Supporto' },
            { slug: 'projects', title: 'Projects', title_it: 'Progetti' },
            { slug: 'dna-compare', title: 'DNA Compare', title_it: 'Confronta DNA' }
          ],
          company: [
            { slug: 'about', title: 'About', title_it: 'Chi Siamo' },
            { slug: 'contact', title: 'Contact', title_it: 'Contatti' }
          ],
          legal: [
            { slug: 'privacy', title: 'Privacy', title_it: 'Privacy' },
            { slug: 'terms', title: 'Terms', title_it: 'Termini di Servizio' }
          ]
        });
      }
    };

    fetchBlueprintData();
  }, []);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
  };

  const handleCountryChange = (countryCode) => {
    setCountry(countryCode);
    localStorage.setItem('userCountry', countryCode);
  };

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];
  
  // Get localized description
  const description = i18n.language === 'it' 
    ? branding.footer_description_it 
    : branding.footer_description;

  // Use slug directly as route - NO MAPPING, CMS controls everything
  const getPageRoute = (slug) => {
    return `/${slug}`;
  };

  // Get display title
  const getPageTitle = (page) => {
    return i18n.language === 'it' ? (page.title_it || page.title) : page.title;
  };

  // Column titles
  const columnTitles = {
    explore: i18n.language === 'it' ? 'Esplora' : 'Explore',
    resources: i18n.language === 'it' ? 'Risorse' : 'Resources',
    company: i18n.language === 'it' ? 'Azienda' : 'Company',
    legal: i18n.language === 'it' ? 'Legale' : 'Legal'
  };

  return (
    <footer className="mt-auto bg-[#121212]">
      {/* Main Footer Content */}
      <div className="border-t border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            
            {/* Brand Column */}
            <div className="col-span-1 md:col-span-1">
              {branding.logo_url ? (
                <img 
                  src={branding.logo_url} 
                  alt={branding.site_name} 
                  className="h-8 w-auto mb-4" 
                />
              ) : (
                <h3 className="text-lg font-medium text-white mb-4">
                  {branding.site_name}
                </h3>
              )}
              <p className="text-white/40 text-sm leading-relaxed">
                {description}
              </p>
            </div>
            
            {/* Explore Column */}
            {footerPages.explore.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-4">
                  {columnTitles.explore}
                </h4>
                <ul className="space-y-3">
                  {footerPages.explore.map(page => (
                    <li key={page.slug}>
                      <Link 
                        to={getPageRoute(page.slug)}
                        className="text-sm text-white/40 hover:text-[#B08B5C] transition-colors"
                      >
                        {getPageTitle(page)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Resources Column */}
            {footerPages.resources.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-4">
                  {columnTitles.resources}
                </h4>
                <ul className="space-y-3">
                  {footerPages.resources.map(page => (
                    <li key={page.slug}>
                      <Link 
                        to={getPageRoute(page.slug)}
                        className="text-sm text-white/40 hover:text-[#B08B5C] transition-colors"
                      >
                        {getPageTitle(page)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Company / Legal Column */}
            <div>
              {footerPages.company.length > 0 && (
                <>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-4">
                    {columnTitles.company}
                  </h4>
                  <ul className="space-y-3 mb-6">
                    {footerPages.company.map(page => (
                      <li key={page.slug}>
                        <Link 
                          to={getPageRoute(page.slug)}
                          className="text-sm text-white/40 hover:text-[#B08B5C] transition-colors"
                        >
                          {getPageTitle(page)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="border-t border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            
            {/* Language & Country Selectors */}
            <div className="flex items-center gap-4">
              <Globe className="w-4 h-4 text-white/30" />
              
              {/* Language */}
              <Select value={i18n.language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-[130px] h-9 bg-[#121212] border-[#1a1a1a] text-white/60 text-sm">
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      <span>{currentLang.flag}</span>
                      <span>{currentLang.name}</span>
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1a1a1a]">
                  {LANGUAGES.map(lang => (
                    <SelectItem 
                      key={lang.code} 
                      value={lang.code}
                      className="text-white/60 hover:text-white hover:bg-[#1a1a1a] cursor-pointer"
                    >
                      <span className="flex items-center gap-2">
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Country */}
              <Select value={country} onValueChange={handleCountryChange}>
                <SelectTrigger className="w-[160px] h-9 bg-[#121212] border-[#1a1a1a] text-white/60 text-sm">
                  <SelectValue placeholder={i18n.language === 'it' ? 'Seleziona paese' : 'Select country'} />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1a1a1a]">
                  {COUNTRIES.map(c => (
                    <SelectItem 
                      key={c.code} 
                      value={c.code}
                      className="text-white/60 hover:text-white hover:bg-[#1a1a1a] cursor-pointer"
                    >
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Legal Links & Copyright */}
            <div className="flex items-center gap-6">
              {footerPages.legal.map(page => (
                <Link 
                  key={page.slug}
                  to={getPageRoute(page.slug)}
                  className="text-xs text-white/30 hover:text-white/50 transition-colors"
                >
                  {getPageTitle(page)}
                </Link>
              ))}
              
              <span className="text-xs text-white/20">
                {branding.copyright_text || `© ${currentYear} ${branding.site_name}. All rights reserved.`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
